import { useState, useRef, useEffect, useCallback } from 'react'
import { LABEL_COLORS } from '@/constants/LabelColors'
import { getNextLabelColor } from '@/utils/LabelColors'
import './label-popover.css'

interface LabelCreatePopoverProps {
	anchorRef: React.RefObject<HTMLElement | null>
	existingColors: string[]
	onCreateLabel: (data: { name: string; color: string }) => Promise<void>
	onClose: () => void
}

export function LabelCreatePopover({
	anchorRef,
	existingColors,
	onCreateLabel,
	onClose,
}: LabelCreatePopoverProps) {
	const [name, setName] = useState('')
	const [color, setColor] = useState(() => getNextLabelColor(existingColors))
	const [isCreating, setIsCreating] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		inputRef.current?.focus()
	}, [])

	const handleClose = useCallback(() => onClose(), [onClose])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node) &&
				!anchorRef.current?.contains(e.target as Node)
			) {
				handleClose()
			}
		}
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleClose()
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [handleClose, anchorRef])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim() || isCreating) return
		setIsCreating(true)
		try {
			await onCreateLabel({ name: name.trim(), color })
		} finally {
			setIsCreating(false)
		}
	}

	return (
		<div ref={popoverRef} className="label-popover">
			<form onSubmit={handleSubmit} className="label-create-form">
				<input
					ref={inputRef}
					type="text"
					placeholder="Label name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					className="label-create-input"
					maxLength={100}
				/>
				<div className="label-color-swatches" role="radiogroup" aria-label="Label color">
					{LABEL_COLORS.map((c) => (
						<button
							key={c}
							type="button"
							className="label-color-swatch"
							data-selected={color === c}
							style={{ backgroundColor: c }}
							onClick={() => setColor(c)}
							role="radio"
							aria-checked={color === c}
							aria-label={`Color ${c}`}
						/>
					))}
				</div>
				<button
					type="submit"
					className="label-create-submit"
					disabled={!name.trim() || isCreating}
				>
					Create
				</button>
			</form>
		</div>
	)
}
